import { defineStore } from 'pinia'
import * as Sentry from "@sentry/vue"
import tokensApi from '../services/api/tokens/tokens';
import employeeApi from '@/services/api/settings/employees/employees'
import { useSchedulerStore } from './scheduler'
import { isTokenExpired } from '@/utils/jwt'

const getDefaultState = () => ({
  profile: null,
  role: null,
  sophiaToken: null,
  idAssistant: null,
  token: null,
  isAdmin: false,
  attendanceStatus: null, // 0 - Out || 1 - In (0 fichaje inactivo, 1 fichaje activo)
  //user: null
  notificationCount: 0,
  // Settings de shell devueltos por LayoutController::getLayout. Hoy contiene:
  //  - SETTINGS_INACTIVITY_TIME
  //  - SETTINGS_APPLICATION_LAYOUT_MODE
  //  - SETTINGS_NOTIFICATIONS_GLOBAL (decide canal Pusher per-center vs tenant-wide)
  // Se persisten en el store para que codigo no-Vue (ej. servicios de
  // broadcasting) pueda leerlos sin tener que re-pedirlos.
  shellSettings: {},
  _sophiaTokensInflight: null,
  _sophiaTokensFetchedOnce: false,
  _attendanceStatusInflight: null,
  _attendanceStatusFetchedOnce: false,
});

export const useUserStore = defineStore('user', {
  state: getDefaultState,
  // sophiaToken e idAssistant no se persisten: se rellenan solo cuando el layout hace getTokens()
  // attendanceStatus tampoco se persiste para evitar estado stale entre logout/login
  persist: { omit: ['sophiaToken', 'idAssistant', 'attendanceStatus', '_attendanceStatusFetchedOnce'] },
  actions: {
    setLogin(payload) {

      const { user, token } = payload;
      
      this.profile = user;
      this.token = token;
      this.isAdmin = user.admin || false;
      
       localStorage.setItem("userToken", token);

      if (import.meta.env.VITE_APP_ENV === "production") {

        Sentry.setUser({
          id: `${user.customer_code}_${user.id}`,
          email: user.email,
      });
      }

      // ✅ Forzar recarga del scheduler con la config del nuevo usuario
      const schedulerStore = useSchedulerStore();
      schedulerStore.$hydrate();

    },
    setProfile(user) {
      this.profile = user;
      this.isAdmin = user.admin;
    },
    setAttendanceStatus(attendanceStatus) {
      this.attendanceStatus = attendanceStatus
    },
    setToken(payload){

      const { token } = payload
      this.token = token

    },
    setSophiaToken(token) {
      this.sophiaToken = token ?? null;
    },
    updateSophiaToken(sophiaToken) {
      this.sophiaToken = sophiaToken ?? null;
    },
    setAssistantId(idAssistant) {
      this.idAssistant = idAssistant ?? null;
    },
    setShellSettings(shellSettings) {
      this.shellSettings = shellSettings && typeof shellSettings === 'object'
        ? { ...shellSettings }
        : {};
    },

    // Lazy: antes AuthenticatedLayout pedia estos tokens al arranque aunque
    // la mayoria de usuarios nunca abre el asistente. Ahora solo se cargan
    // la primera vez que se abre el modal de Sophia.
    // Nota: el credito puede ser 0, asi que no podemos usar `sophiaToken != null`
    // para saber si ya vino de API; usamos un flag.
    async ensureSophiaTokens() {
      if (this._sophiaTokensFetchedOnce) return;
      if (this._sophiaTokensInflight) return this._sophiaTokensInflight;
      this._sophiaTokensInflight = tokensApi.getTokens()
        .then(res => {
          this.setSophiaToken(res.data?.sophia_tokens);
          this.setAssistantId(res.data?.sophia_assistant_ids);
        })
        .catch(err => { console.error('Error cargando sophia tokens (lazy):', err); })
        .finally(() => {
          this._sophiaTokensInflight = null;
          this._sophiaTokensFetchedOnce = true;
        });
      return this._sophiaTokensInflight;
    },

    // Lazy: el Navbar pide el estado de fichaje (0/1) al montarse, fuera del
    // `Promise.all` critico de AuthenticatedLayout. El 0 (sin fichar) es un
    // valor valido, por eso usamos flag en vez de `attendanceStatus != null`.
    async ensureAttendanceStatus() {
      if (this._attendanceStatusFetchedOnce) return;
      if (this._attendanceStatusInflight) return this._attendanceStatusInflight;
      if (!this.profile?.id) return;
      this._attendanceStatusInflight = employeeApi.attendanceStatus(this.profile.id)
        .then(res => this.setAttendanceStatus(res.data))
        .catch(err => { console.error('Error cargando attendanceStatus (lazy):', err); })
        .finally(() => {
          this._attendanceStatusInflight = null;
          this._attendanceStatusFetchedOnce = true;
        });
      return this._attendanceStatusInflight;
    },

    setLogout() {
      this.$reset();
      localStorage.removeItem("userToken");
    },
  },
  getters: {
    // Token presente y no expirado (evita entrar a rutas protegidas al día siguiente sin hacer todas las peticiones 401)
    isAuthenticated: (state) => !!state.token && !isTokenExpired(state.token),
    getProfile: (state) => state.profile,
    getIsAdmin: (state) => state.isAdmin,
    getAttendanceStatus: (state) => state.attendanceStatus,
    getSophiaToken: (state) => state.sophiaToken,
  },
})